import { BudgetEstimateComponent } from '@/components/shared/budget-estimate/budget-estimate.component';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  FileFieldProps,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  DateFieldProps,
  FileRepresentation,
} from '../../../../components/form-builder/form-builder.types';
import { MultiFormAccordionComponent } from '../../../../components/multi-form-accordion/multi-form-accordion.component';
import { PpmRepository } from '../../../../store/ppm-state/ppm-state.repository';
import { ResponseDoc } from '@/types';
import { map, of } from 'rxjs';
import { PpmMilestoneFormComponent } from '../../milestone-form/milestone-form.component';
import { FileRemoveEvent } from 'node_modules/primeng/fileupload/public_api';

interface getPpmInspectPlanFormObjectProps {
  store: PpmRepository;
  acOptions: SelectOption[];
  download?: (file: FileRepresentation) => void;
  openFile: (file: FileRepresentation) => void;
}

export const getPpmInspectPlanFormObject = ({ store, download, acOptions, openFile }: getPpmInspectPlanFormObjectProps): DynamicForm => {
  const isDisabled = of(true);

  const refField: DynamicField<TextFieldProps> = {
    label: 'ppm.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable('errors', 'ref'),
    props: {
      value: store.getNestedObservable<string>('dto', 'ref'),
      placeholder: 'ppm.form.placeholders.sysName',
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'ppm.form.year',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable('errors', 'annee'),
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      view: 'year',
      format: 'yy',
    },
  };

  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'ppm.form.ac_options',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable<string>('errors', 'acId'),
    props: {
      value: store.getObservable('acsSelected'),
      placeholder: 'ppm.form.placeholders.ac_options',
      options: acOptions,
      filterEnabled: true,
    },
  };

  const milestoneField: DynamicField<CustomFieldProps> = {
    label: 'ppm.form.milestone',
    variant: FieldVariant.CUSTOM,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['ppm.form.milestone2'],
        component: PpmMilestoneFormComponent,
        injectKeys: ['isDisabled', 'acId', 'actualizedCount'],
        isDisabled,
        acId: store.getNestedObservable<number>('dto', 'acId'),
        actualizedCount: store.getNestedObservable<number>('response', 'nbrActualisation'),
        downSyndromeComponent: BudgetEstimateComponent,
        downSyndromeProps: {
          label: 'milestone.form.estimated_total_budget',
          value: store.getObservable<number>('totalEstimatedBudget'),
        },
      },
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'ppm.form.files',
    variant: FieldVariant.FILE,
    isDisabled,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'files'),
      files: store
        .getNestedObservable<ResponseDoc[]>('response', 'docs')
        .pipe(
          map(docs =>
            docs.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),
      openFile: (file: FileRepresentation) => openFile?.(file),
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'dto',
          'ppmFiles',
          store.getNested<File[]>('dto', 'ppmFiles').filter(file => file.name !== event.file.name)
        );
      },
    },
  };
  const notesField: DynamicField<EditorFieldProps> = {
    label: 'ppm.form.remarks',
    variant: FieldVariant.EDITOR,
    isDisabled,
    props: {
      height: '320px',
    },
  };

  return {
    title: 'Plan de passasion des achats de cette année',
    description: 'Gérez et suivez votre plan annuel des achats pour cette année, Modifiez, mettez  à jour ou visualisez.',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [acOptionsField],
          },
          {
            fields: [yearField, refField],
          },
        ],
      },
      {
        title: 'Réalisation envisagée',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [milestoneField],
          },
        ],
      },
      {
        title: 'Entrée secondaire',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [notesField],
          },
        ],
      },
    ],
  };
};
